import React, { useState, useEffect, useRef, useMemo } from 'react';
import { OrganizationSwitcher, useOrganizationList } from '@clerk/react';
import { Menu, Zap, Bell, Search } from 'lucide-react';
import { UserRole, requiresOperatorClerkOrg } from '@repo/shared-types';
import { useTheme } from './ThemeContext';
import { CommandPalette } from './CommandPalette';
import { NavigationSidebar } from './NavigationSidebar';
import { TenantMobileNav } from './TenantMobileNav';
import { useAppStore } from '../../store';
import { Outlet, useNavigate } from '../../routes';
import { useAppPathname } from '../../navigation/useAppPathname';
import { getActiveNavIdFromPath } from '@/utils/navigation';
import { canAccessRoute } from '@/utils/authorization';
import { useSignOut } from '../../contexts/SignOutContext';

import { useNotifications, useMarkNotificationRead, useClearNotifications } from '../../hooks/queries/useCommunication';
import { useCurrentUser } from '../../hooks/queries/useUsers';
import { getRouteFromViewState, getNavSidebarItemsForLayout } from '@repo/shared-config';
import { useNavBadgeCounts } from '../../hooks/useNavBadgeCounts';
import { useOrgNavFeatures } from '../../hooks/useOrgNavFeatures';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import { isDemoMode } from '../../utils/demoClock';

export const Layout: React.FC = () => {
  const navigate = useNavigate();
  const pathname = useAppPathname();
  const signOut = useSignOut();
  const { userRole, setUserRole, currentUserId, isSelfManagingOwner } = useAppStore();

  // Queries
  const { data: notifications = [] } = useNotifications();
  const { data: currentUser, isLoading: loadingUser } = useCurrentUser();
  const markReadMutation = useMarkNotificationRead();
  const clearNotificationsMutation = useClearNotifications();

  const orgNavFeatures = useOrgNavFeatures();
  const navLeafBadgeCounts = useNavBadgeCounts(userRole);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCmdPaletteOpen, setIsCmdPaletteOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [loadingNavId, setLoadingNavId] = useState<string | null>(null);
  const { theme, toggleTheme } = useTheme();

  const [sidebarDetailCollapsed, setSidebarDetailCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const raw = localStorage.getItem('app-sidebar-detail-collapsed');
    return raw === 'true';
  });
  const handleSidebarDetailCollapseToggle = () => {
    setSidebarDetailCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('app-sidebar-detail-collapsed', String(next));
      return next;
    });
  };

  const [sidebarRailExpanded, setSidebarRailExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    const raw = localStorage.getItem('app-sidebar-rail-expanded');
    return raw === 'true';
  });
  const handleSidebarRailExpandToggle = () => {
    setSidebarRailExpanded((prev) => {
      const next = !prev;
      localStorage.setItem('app-sidebar-rail-expanded', String(next));
      return next;
    });
  };

  const notifRef = useRef<HTMLDivElement>(null);

  const { userMemberships, isLoaded: orgListLoaded } = useOrganizationList();
  const demoSingleOrg =
    isDemoMode() && orgListLoaded && (userMemberships.data?.length ?? 0) <= 1;
  const showOperatorOrgSwitcher =
    requiresOperatorClerkOrg(userRole, isSelfManagingOwner === true) && !demoSingleOrg;

  useEffect(() => {
    // User context logging removed - use logger if needed for debugging
  }, [currentUserId, userRole]);

  const handleNavigate = (view: string, _context?: Record<string, unknown>) => {
    setLoadingNavId(view);
    let path = getRouteFromViewState(view);

    // Role-specific routing overrides
    if (view === 'maintenance.work_orders' && userRole === UserRole.TENANT) {
      path = '/app/my-maintenance';
    }
    if (view === 'accounting.bills' && isSelfManagingOwner) {
      path = ROUTES.OPS_ACCOUNTING_BILLS;
    }

    navigate(path);
    if (window.innerWidth < 768) setMobileMenuOpen(false);
  };

  const currentNavId = getActiveNavIdFromPath(pathname);

  // Clear loading state when route changes
  useEffect(() => {
    if (loadingNavId) {
      // Clear loading after a short delay or when nav matches
      const timer = setTimeout(() => {
        setLoadingNavId(null);
      }, 500); // Increased timeout to allow route to fully load
      return () => clearTimeout(timer);
    }
  }, [currentNavId, loadingNavId]);

  // Also clear loading when pathname changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingNavId(null);
    }, 1000);
    return () => clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCmdPaletteOpen(true);
      }
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setIsNotifOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Dynamically build nav config with conditional Units visibility and permission filtering
  const currentNav = useMemo(() => {
    const baseNav = getNavSidebarItemsForLayout(userRole, Boolean(isSelfManagingOwner), {
      orgFeatures: orgNavFeatures,
    });

    return baseNav
      .map((item) => {
        const parentRoute = getRouteFromViewState(item.id);
        const canAccessParent = canAccessRoute(userRole, parentRoute, isSelfManagingOwner ?? false);

        let filteredChildren: typeof item.children | undefined;
        if (item.children) {
          filteredChildren = item.children.filter((child) => {
            const childRoute = getRouteFromViewState(child.id);
            return canAccessRoute(userRole, childRoute, isSelfManagingOwner ?? false);
          });
        }

        if (canAccessParent || (filteredChildren && filteredChildren.length > 0)) {
          return { ...item, children: filteredChildren };
        }

        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [userRole, isSelfManagingOwner, orgNavFeatures]);

  const unreadNotifications = notifications.filter((n) => !n.read);

  const handleNotificationClick = (notif: { id: string; read?: boolean; link?: string; context?: Record<string, unknown> }) => {
    markReadMutation.mutate(notif.id);
    if (notif.link) {
      handleNavigate(notif.link, notif.context);
      setIsNotifOpen(false);
    }
  };

  return (
    <div className="flex h-screen w-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
      <CommandPalette
        isOpen={isCmdPaletteOpen}
        onClose={() => setIsCmdPaletteOpen(false)}
      />

      {/* Sidebar */}
      <NavigationSidebar
        currentNav={currentNav}
        currentNavId={currentNavId}
        onNavigate={handleNavigate}
        loadingNavId={loadingNavId}
        userRole={userRole}
        onRoleChange={(role) => {
          setUserRole(role);
          navigate('/app');
        }}
        theme={theme}
        onToggleTheme={toggleTheme}
        sidebarDetailCollapsed={sidebarDetailCollapsed}
        onSidebarDetailCollapseToggle={handleSidebarDetailCollapseToggle}
        sidebarRailExpanded={sidebarRailExpanded}
        onSidebarRailExpandToggle={handleSidebarRailExpandToggle}
        currentUser={currentUser ?? undefined}
        loadingUser={loadingUser}
        onSettingsClick={() => handleNavigate('settings')}
        onSignOut={signOut}
        className={mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        isMobileOpen={mobileMenuOpen}
        navLeafBadgeCounts={navLeafBadgeCounts}
      />

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-950 relative">
        <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-6 flex-shrink-0 z-10 transition-colors duration-200">
          <div className="flex items-center text-slate-500 dark:text-slate-400">
            <button type="button" aria-label="Toggle navigation menu" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="mr-4 md:hidden">
              <Menu className="cursor-pointer text-slate-600 dark:text-slate-300" />
            </button>
            <div
              className="relative hidden md:block group cursor-text"
              onClick={() => setIsCmdPaletteOpen(true)}
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <div
                className="flex items-center pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-800 border-transparent rounded-lg text-sm w-64 lg:w-96 text-slate-500 dark:text-slate-400 hover:bg-white hover:ring-2 hover:ring-brand-500/20 transition-all cursor-text"
              >
                <span>Search everything...</span>
                <span className="ml-auto text-xs border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5">⌘K</span>
              </div>
            </div>
            {isSelfManagingOwner && pathname.startsWith('/app/ops') ? (
              <span className="ml-4 hidden items-center gap-2 md:flex">
                <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  Operations
                </span>
                <button
                  type="button"
                  className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
                  onClick={() => navigate(ROUTES.OWNER_STATEMENTS)}
                >
                  Owner view
                </button>
              </span>
            ) : null}
            {isSelfManagingOwner && pathname.startsWith('/app/owner/') ? (
              <span className="ml-4 hidden items-center gap-2 md:flex">
                <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  Owner
                </span>
                <button
                  type="button"
                  className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
                  onClick={() => navigate(ROUTES.LEASING)}
                >
                  Operations view
                </button>
              </span>
            ) : null}
          </div>
          <div className="flex items-center space-x-4">
            {showOperatorOrgSwitcher ? (
              <div className="operator-org-switcher hidden sm:block min-w-[10rem] max-w-[14rem] shrink-0">
                <OrganizationSwitcher hidePersonal afterLeaveOrganizationUrl={ROUTES.SELECT_ORG} />
              </div>
            ) : null}

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setIsNotifOpen(!isNotifOpen)}
                className={`p-2 relative rounded-full transition ${isNotifOpen ? 'bg-slate-100 dark:bg-slate-800 text-brand-600 dark:text-brand-400' : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'}`}
              >
                <Bell size={20} />
                {unreadNotifications.length > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-slate-900 animate-pulse"></span>
                )}
              </button>

              {/* Notification Dropdown */}
              {isNotifOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 z-50 overflow-hidden animate-slide-up origin-top-right">
                  <div className="p-3 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">Notifications</h3>
                    {notifications.length > 0 && (
                      <button onClick={() => clearNotificationsMutation.mutate()} className="text-xs text-slate-500 hover:text-brand-600 dark:hover:text-brand-400">Clear All</button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          onClick={() => handleNotificationClick(notif)}
                          className={`p-4 border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition ${notif.read ? '' : 'bg-brand-50/50 dark:bg-brand-900/10'}`}
                        >
                          <div className="flex items-start">
                            <div className={`mt-0.5 w-2 h-2 rounded-full mr-3 shrink-0 ${notif.type === 'success' ? 'bg-emerald-500' :
                              notif.type === 'warning' ? 'bg-amber-500' :
                                notif.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                              }`}></div>
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${notif.read ? 'text-slate-600 dark:text-slate-400' : 'text-slate-900 dark:text-white'}`}>{notif.title}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">{notif.message}</p>
                              <p className="text-[10px] text-slate-400 mt-2">{notif.timestamp}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                        <Bell size={24} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No new notifications</p>
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t border-slate-100 dark:border-slate-800 text-center">
                    <button className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline">View All Activity</button>
                  </div>
                </div>
              )}
            </div>

            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <button className="hidden sm:inline-flex items-center px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition shadow-sm hover:shadow-md">
              <Zap size={16} className="mr-2" />
              Quick Action
            </button>
          </div>
        </header>

        {/* Skip Links for Keyboard Navigation */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 bg-brand-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          onClick={(e) => {
            e.preventDefault();
            const mainContent = document.getElementById('main-content');
            if (mainContent) {
              mainContent.focus();
              mainContent.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          Skip to main content
        </a>

        <div
          id="main-content"
          className={`flex-1 overflow-auto p-6 md:p-8 ${userRole === UserRole.TENANT ? 'pb-20 md:pb-8' : ''}`}
          tabIndex={-1}
        >
          <div className={`${pathname === '/app/properties' ? 'max-w-[95%]' : 'max-w-7xl'} mx-auto h-full w-full`}>
            <Outlet />
          </div>
        </div>
      </main>
      {userRole === UserRole.TENANT ? (
        <TenantMobileNav onNavigate={(path) => navigate(path)} />
      ) : null}
    </div>
  );
};


